/**
 * Column config for registrant portal Invoices tab (operator-issued invoices).
 */

import type { ColumnProperties } from "@/components/non_registrant/dashboard-table";
import { Badge } from "@/components/ui/badge";
import type { RegistrantPortalInvoiceListItemSchema } from "@/hera-api/client/types.gen";
import {
  formatCurrency,
  formatDateNoTimezone,
  formatDateTime,
} from "@/lib/utils";

const MULTIPLE_VALUES_LABEL = "Multiple Licenses";

function InvoiceListCell({ value }: { value: string }) {
  if (value === MULTIPLE_VALUES_LABEL) {
    return (
      <span className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground italic">
        {value}
      </span>
    );
  }
  return <span>{value}</span>;
}

export const REGISTRANT_INVOICES_ACCESSOR_KEYS = [
  "invoice_id",
  "property_address",
  "payment_state",
  "status",
  "license_display_id",
  "jurisdiction_name",
  "license_type_name",
  "ams_company_name",
  "total_amount",
  "created_at",
  "payment_date",
] as const;

export type RegistrantInvoicesAccessorKeys =
  (typeof REGISTRANT_INVOICES_ACCESSOR_KEYS)[number];

export type RegistrantInvoiceColumnProperties = ColumnProperties<
  RegistrantPortalInvoiceListItemSchema,
  RegistrantInvoicesAccessorKeys
>;

function paymentStateBadgeVariant(
  state: RegistrantPortalInvoiceListItemSchema["payment_state"],
) {
  if (state === "paid") {
    return "default" as const;
  }
  if (state === "cancelled") {
    return "destructive" as const;
  }
  return "secondary" as const;
}

function paymentStateLabel(
  state: RegistrantPortalInvoiceListItemSchema["payment_state"],
) {
  if (state === "paid") {
    return "Paid";
  }
  if (state === "cancelled") {
    return "Cancelled";
  }
  return "Unpaid";
}

function invoiceRowTotal(row: RegistrantPortalInvoiceListItemSchema): number {
  const reg = Number.parseFloat(row.total_registration_fee) || 0;
  const late = Number.parseFloat(row.total_late_fee) || 0;
  const proc = Number.parseFloat(row.total_processing_fee) || 0;
  const ins = Number.parseFloat(row.total_inspection_fee) || 0;
  return reg + late + proc + ins;
}

export function getRegistrantInvoiceColumnProperties(
  accessorKey: RegistrantInvoicesAccessorKeys,
): RegistrantInvoiceColumnProperties {
  switch (accessorKey) {
    case "invoice_id":
      return {
        displayName: "Invoice",
        defaultVisible: true,
        getValue: (row, _key) => row.invoice_id,
        renderCell: (row) => <InvoiceListCell value={row.invoice_id || "—"} />,
      };
    case "payment_state":
      return {
        displayName: "Payment",
        defaultVisible: true,
        allowBlankFilter: false,
        getValue: (row, _key) => paymentStateLabel(row.payment_state),
        renderCell: (row) => (
          <Badge variant={paymentStateBadgeVariant(row.payment_state)}>
            {paymentStateLabel(row.payment_state)}
          </Badge>
        ),
      };
    case "status":
      return {
        displayName: "Status",
        defaultVisible: true,
        allowBlankFilter: false,
        getValue: (row, _key) => row.status,
        renderCell: (row) => <span>{row.status}</span>,
      };
    case "license_display_id":
      return {
        displayName: "Registration",
        defaultVisible: true,
        getValue: (row, _key) => row.license_display_id,
        renderCell: (row) => (
          <InvoiceListCell value={row.license_display_id || "—"} />
        ),
      };
    case "jurisdiction_name":
      return {
        displayName: "Jurisdiction",
        defaultVisible: true,
        allowBlankFilter: false,
        getValue: (row, _key) => row.jurisdiction_name,
        renderCell: (row) => <span>{row.jurisdiction_name}</span>,
      };
    case "property_address":
      return {
        displayName: "Property",
        defaultVisible: true,
        getValue: (row, _key) => row.property_address ?? "",
        renderCell: (row) => (
          <span className="max-w-[240px] truncate block">
            {row.property_address ?? "—"}
          </span>
        ),
      };
    case "license_type_name":
      return {
        displayName: "License Type",
        defaultVisible: true,
        allowBlankFilter: false,
        getValue: (row, _key) => row.license_type_name,
        renderCell: (row) => <span>{row.license_type_name}</span>,
      };
    case "ams_company_name":
      return {
        displayName: "AMS",
        defaultVisible: true,
        getValue: (row, _key) => row.ams_company_name ?? "",
        renderCell: (row) => <span>{row.ams_company_name ?? "—"}</span>,
      };
    case "total_amount":
      return {
        displayName: "Total",
        defaultVisible: true,
        allowBlankFilter: false,
        getValue: (row, _key) => String(invoiceRowTotal(row)),
        renderCell: (row) => (
          <span className="tabular-nums">
            {formatCurrency(invoiceRowTotal(row))}
          </span>
        ),
      };
    case "created_at":
      return {
        displayName: "Issued",
        defaultVisible: true,
        allowBlankFilter: false,
        getValue: (row, _key) => row.created_at,
        renderCell: (row) => (
          <span className="whitespace-nowrap">
            {formatDateTime(row.created_at)}
          </span>
        ),
      };
    case "payment_date":
      return {
        displayName: "Paid On",
        defaultVisible: true,
        getValue: (row, _key) => row.payment_date ?? "",
        renderCell: (row) => (
          <span className="whitespace-nowrap">
            {row.payment_date ? formatDateNoTimezone(row.payment_date) : "—"}
          </span>
        ),
      };
  }
}
